import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Dialog } from '@src/components/dialog';
import moment from 'moment';
import { useState } from 'react';
import { SubscriptionItem } from '@src/api/subscription';
import { useSubscriptionService } from './hooks';

const PLAN_COLORS: Record<string, { bgcolor: string; color: string }> = {
  'Day VIP': { bgcolor: '#FFF3E0', color: '#E65100' },
  'Weekly VIP': { bgcolor: '#EDE8FF', color: '#6A00AC' },
  'Monthly VIP': { bgcolor: '#E6F9F0', color: '#00875A' },
};

// ---------- Stepper ----------
const STEPS = ['Details', 'Confirm', 'Complete'];

const StepIndicator = ({ step, currentStep }: { step: number; currentStep: number }) => {
  const done = currentStep > step;
  const active = currentStep === step;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: "column" }}>
      <Box
        sx={{
          width: 30, height: 30,
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: done || active ? '#4F39F6' : 'transparent',
          border: done || active ? 'none' : '2px solid #D1D5DB',
          color: done || active ? 'white' : '#9CA3AF',
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        {done ? <CheckIcon sx={{ fontSize: 18 }} /> : step}
      </Box>
      <Typography
        fontSize={12}
        fontWeight={active ? 700 : 500}
        color={active ? '#4F39F6' : done ? '#4F39F6' : '#9CA3AF'}
        ml={0.75}
        mt={0.6}
      >
        {STEPS[step - 1]}
      </Typography>
    </Box>
  );
};

const StepLine = ({ done }: { done: boolean }) => (
  <Box sx={{ flex: 1, height: 2, bgcolor: done ? '#6A00AC' : '#E5E7EF', mx: 1 }} />
);

const StepperRow = ({ step }: { step: number }) => (
  <Stack direction="row" alignItems="center" mb={3} width="50%">
    <StepIndicator step={1} currentStep={step} />
    <StepLine done={step > 1} />
    <StepIndicator step={2} currentStep={step} />
    <StepLine done={step > 2} />
    <StepIndicator step={3} currentStep={step} />
  </Stack>
);

// ---------- Shared user summary bar ----------
type SubSummaryProps = { item: SubscriptionItem };

const SubscriptionSummaryBar = ({ item }: SubSummaryProps) => {
  const planLabel = item?.subscription?.name;
  const planStyle = PLAN_COLORS[planLabel] || { bgcolor: '#F3F4F6', color: '#6B7280' };
  const expiresFromNow = item.expiresAt
    ? `${moment(item.expiresAt).diff(moment(), 'days')} days`
    : '';

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography fontSize={15} fontWeight={700} color="#111827">
            {item.user?.displayName ?? 'Unknown'}
          </Typography>
          <Typography fontSize={12} color="#6B7280">
            User ID: {item.user?.id}
          </Typography>
        </Box>
        <Chip
          label={planLabel}
          size="small"
          sx={{ bgcolor: planStyle.bgcolor, color: planStyle.color, fontWeight: 700, fontSize: 12, borderRadius: '6px' }}
        />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
        {[
          { label: 'Status', value: item.status, color: item.status === 'ACTIVE' ? '#00875A' : '#DE350B' },
          { label: 'Plan', value: planLabel },
          { label: 'Start Date', value: item.startedAt ? moment.parseZone(item.startedAt).format('MM-DD-YYYY') + '\n' + moment.parseZone(item.startedAt).format('hh:mm A') : '-' },
          { label: 'Expires On (Auto)', value: item.expiresAt ? moment.parseZone(item.expiresAt).format('MM-DD-YYYY') : '-', sub: expiresFromNow ? `in ${expiresFromNow}` : '' },
          { label: 'Amount', value: `€${item?.subscription?.price}` },
        ].map(({ label, value, sub, color }) => (
          <Box key={label}>
            <Typography fontSize={10} color="#9CA3AF" fontWeight={400} textTransform="uppercase" letterSpacing={0.5} mb={0.25}>
              {label}
            </Typography>
            <Typography fontSize={13} fontWeight={500} color={color ?? '#111827'} whiteSpace="pre-line">
              {value}
            </Typography>
            {sub && <Typography fontSize={11} color="#9CA3AF">{sub}</Typography>}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---------- Step 1: Details ----------
type Step1Props = {
  item: SubscriptionItem;
  notes: string;
  onNotesChange: (v: string) => void;
  onCancel: () => void;
  onContinue: () => void;
};

const StepDetails = ({ item, notes, onNotesChange, onCancel, onContinue }: Step1Props) => (
  <Box>
    <StepperRow step={1} />
    <SubscriptionSummaryBar item={item} />
    <Divider sx={{ my: 2.5 }} />

    <Box sx={{ gridTemplateColumns: '1fr 1fr', gap: 2 }}>

      <Box>
        <Typography fontSize={13} fontWeight={500} color="#374151" mb={0.75}>
          Additional Notes <Typography component="span" color="#9CA3AF">(optional)</Typography>
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value.slice(0, 250))}
            placeholder="Add any additional notes here..."
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 },
            }}
          />
          <Typography
            fontSize={11}
            color="#9CA3AF"
            sx={{ position: 'absolute', bottom: 6, right: 10 }}
          >
            {notes.length}/250
          </Typography>
        </Box>
      </Box>
    </Box>

    <Divider sx={{ my: 2.5 }} />
    <Stack direction="row" gap={2}>
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        onClick={onCancel}
        sx={{ borderRadius: '8px', height: 44, fontWeight: 700, fontSize: 13 }}
      >
        CANCEL
      </Button>
      <Button
        fullWidth
        variant="contained"
        onClick={onContinue}
        sx={{ borderRadius: '8px', height: 44, fontWeight: 700, fontSize: 13, bgcolor: '#6A00AC', '&:hover': { bgcolor: '#5200 88' } }}
      >
        CONTINUE
      </Button>
    </Stack>
  </Box>
);

// ---------- Step 2: Confirm ----------
type Step2Props = {
  item: SubscriptionItem;
  confirmText: string;
  onConfirmTextChange: (v: string) => void;
  onBack: () => void;
  onDeactivate: () => void;
  isLoading: boolean;
};

const StepConfirm = ({ item, confirmText, onConfirmTextChange, onBack, onDeactivate, isLoading }: Step2Props) => {
  const isConfirmed = confirmText === 'CONFIRM';

  return (
    <Box>
      <StepperRow step={2} />

      {/* Warning box */}
      <Box
        sx={{
          bgcolor: '#FFFBEB',
          border: '1px solid #FCD34D',
          borderRadius: '10px',
          p: 2,
          mb: 2.5,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <WarningAmberIcon sx={{ color: '#D97706', fontSize: 20 }} />
          <Typography fontSize={14} fontWeight={700} color="#92400E">
            Are you sure?
          </Typography>
        </Stack>
        <Typography fontSize={13} color="#92400E" mb={0.5}>
          This action will deactivate the subscription immediately.
        </Typography>
        {[
          'The user will lose all subscription benefits.',
          'This action cannot be undone.',
        ].map((line) => (
          <Typography key={line} fontSize={13} color="#92400E">
            * {line}
          </Typography>
        ))}
        <Typography fontSize={13} color="#92400E">
          * Payment provider will{' '}
          <Typography component="span" fontWeight={800} sx={{ textDecoration: 'underline' }}>
            NOT
          </Typography>{' '}
          automatically refund.
        </Typography>
      </Box>

      <SubscriptionSummaryBar item={item} />
      <Divider sx={{ my: 2.5 }} />

      <Typography fontSize={13} color="#374151" mb={1}>
        Type{' '}
        <Typography component="span" fontWeight={700}>
          CONFIRM
        </Typography>{' '}
        to proceed
      </Typography>
      <TextField
        fullWidth
        value={confirmText}
        onChange={(e) => onConfirmTextChange(e.target.value)}
        placeholder="CONFIRM"
        size="small"
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 } }}
      />

      <Divider sx={{ my: 2.5 }} />
      <Stack direction="row" gap={2}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={onBack}
          sx={{ borderRadius: '8px', height: 44, fontWeight: 700, fontSize: 13 }}
        >
          BACK
        </Button>
        <Button
          fullWidth
          variant="contained"
          disabled={!isConfirmed || isLoading}
          onClick={onDeactivate}
          sx={{
            borderRadius: '8px',
            height: 44,
            fontWeight: 700,
            fontSize: 13,
            bgcolor: isConfirmed ? '#DE350B' : '#F3F4F6',
            color: isConfirmed ? 'white' : '#9CA3AF',
            '&:hover': { bgcolor: isConfirmed ? '#BF2600' : '#F3F4F6' },
            '&.Mui-disabled': { bgcolor: '#F3F4F6', color: '#9CA3AF' },
          }}
        >
          {isLoading ? 'DEACTIVATING...' : 'DEACTIVATE'}
        </Button>
      </Stack>
    </Box>
  );
};

// ---------- Step 3: Complete ----------
type Step3Props = {
  item: SubscriptionItem;
  deactivatedBy?: string;
  onClose: () => void;
};

const StepComplete = ({ item, deactivatedBy = 'Admin', onClose }: Step3Props) => {
  const planLabel = item?.subscription?.name;

  return (
    <Box>
      <StepperRow step={4} />

      <Stack alignItems="center" mb={3} mt={4}>
        <Box
          sx={{
            width: 50, height: 50,
            borderRadius: '50%',
            bgcolor: '#33BB48',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 1.5,
          }}
        >
          <CheckIcon sx={{ color: '#FFFFFF', fontSize: 35 }} />
        </Box>
        <Typography fontSize={18} fontWeight={600} color="#111827">
          Subscription Deactivated
        </Typography>
        <Typography fontSize={13} color="#99A1AF">
          The subscription has been deactivated successfully.
        </Typography>
      </Stack>

      <Box sx={{ border: '1px solid #F3F4F6', borderRadius: '10px', overflow: 'hidden' }}>
        {[
          { label: 'User', value: `${item.user?.displayName ?? 'Unknown'} (User ID: ${item.user?.id})` },
          { label: 'Plan', value: planLabel },
          { label: 'Deactivated on', value: moment().format('MMM DD, YYYY hh:mm A') },
          { label: 'Sub will end on', value: item.expiresAt ? moment.parseZone(item.expiresAt).format('MMM DD, YYYY hh:mm A') : '-' },
          { label: 'Deactivated by', value: deactivatedBy },
        ].map(({ label, value }, i, arr) => (
          <Stack
            key={label}
            direction="row"
            sx={{
              px: 2, py: 1.25,
              borderBottom: i < arr.length - 1 ? '1px solid #F3F4F6' : 'none',
            }}
          >
            <Typography fontSize={13} color="#99A1AF" sx={{ width: 140, flexShrink: 0 }}>
              {label}
            </Typography>
            <Typography fontSize={13} fontWeight={500} color="#364153">{value}</Typography>
          </Stack>
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={onClose}
        sx={{
          mt: 3,
          borderRadius: '8px',
          height: 48,
          fontWeight: 700,
          fontSize: 14,
          bgcolor: '#6A00AC',
          '&:hover': { bgcolor: '#520088' },
        }}
      >
        CLOSE
      </Button>
    </Box>
  );
};

// ---------- Step Failed ----------
type StepFailedProps = {
  errorMessage?: string;
  onClose: () => void;
};

const StepFailed = ({ errorMessage, onClose }: StepFailedProps) => (
  <Box sx={{ textAlign: 'center', py: 2 }}>
    <CancelIcon sx={{ fontSize: 64, color: '#DE350B', mb: 1.5 }} />
    <Typography fontSize={20} fontWeight={700} color="#DE350B" mb={1.5}>
      Deactivation Unsuccessful
    </Typography>
    <Typography fontSize={14} color="#374151" mb={0.5}>
      Please contact
    </Typography>
    <Typography fontSize={14} color="#374151" mb={0.5}>
      Payment Gateway Provider immediately.
    </Typography>
    {errorMessage && (
      <Typography fontSize={14} color="#374151" mb={0.5}>
        {errorMessage}
      </Typography>
    )}
    <Button
      variant="contained"
      onClick={onClose}
      sx={{
        mt: 3,
        width: "50%",
        borderRadius: '8px',
        height: 48,
        fontWeight: 700,
        fontSize: 14,
        bgcolor: '#6A00AC',
        '&:hover': { bgcolor: '#520088' },
      }}
    >
      OKAY
    </Button>
  </Box>
);

// ---------- Main modal ----------
type DeactivateModalProps = {
  open: boolean;
  onClose: () => void;
  item: SubscriptionItem | null;
  onConfirmDeactivate: (params: { notes: string }) => Promise<void>;
  deactivatedBy?: string;
};

const DeactivateModal = ({ open, onClose, item, onConfirmDeactivate, deactivatedBy }: DeactivateModalProps) => {
  const [step, setStep] = useState(1);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { onRefresh } = useSubscriptionService();

  const handleClose = () => {
    setStep(1);
    setNotes('');
    setConfirmText('');
    setErrorMessage('');
    onClose();
  };

    const handleCompleted = () => {
    setStep(1);
    setNotes('');
    setConfirmText('');
    setErrorMessage('');
    onClose();
    onRefresh();
  };

  const handleDeactivate = async () => {
    setIsLoading(true);
    try {
      await onConfirmDeactivate({ notes });
      setStep(3);
    } catch (err: any) {
      setErrorMessage(typeof err === 'string' ? err : err?.message || '');
      setStep(4);
    } finally {
      setIsLoading(false);
    }
  };

  if (!item) return null;

  const title =
    step === 1 ? 'DEACTIVATE SUBSCRIPTION' :
    step === 2 ? 'CONFIRM DEACTIVATION' :
    step === 4 ? 'DEACTIVATION UNSUCCESSFUL' :
    'DEACTIVATION COMPLETE';

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      maxWidth="sm"
      dividers={false}
      dialogTitle={
        <Stack direction="row" alignItems="center" justifyContent={step === 4 ? 'flex-end' : 'space-between'}>
          {step !== 4 && (
            <Typography fontSize={14} fontWeight={700} letterSpacing={1} color="#111827">
              {title}
            </Typography>
          )}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
      dialogContent={
        <Box sx={{ width: '100%', px: 1 }}>
          {step === 1 && (
            <StepDetails
              item={item}
              notes={notes}
              onNotesChange={setNotes}
              onCancel={handleClose}
              onContinue={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <StepConfirm
              item={item}
              confirmText={confirmText}
              onConfirmTextChange={setConfirmText}
              onBack={() => setStep(1)}
              onDeactivate={handleDeactivate}
              isLoading={isLoading}
            />
          )}
          {step === 3 && (
            <StepComplete
              item={item}
              deactivatedBy={deactivatedBy}
              onClose={handleCompleted}
            />
          )}
          {step === 4 && (
            <StepFailed
              errorMessage={errorMessage}
              onClose={handleClose}
            />
          )}
        </Box>
      }
    />
  );
};

export default DeactivateModal;
